import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Divider,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  LinearProgress,
  Chip,
} from "@mui/material";
import { Visibility, VisibilityOff, ArrowBack } from "@mui/icons-material";
import Swal from "sweetalert2";
import { registerUser } from "../services/authService.tsx";
import { useNavigate } from "react-router-dom"; // Gunakan untuk navigasi halaman
import ReCAPTCHA from "react-google-recaptcha";
import { motion, AnimatePresence } from "framer-motion";

import "../../../shared/components/Login.css";

const SignUp = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const navigate = useNavigate();

  const images = ["/img/bg-login/BG1.jpg", "/img/bg-login/BG2.jpg", "/img/bg-login/BG3.jpg", "/img/bg-login/BG4.jpg", "/img/bg-login/BG5.jpg", "/img/bg-login/BG6.jpg", "/img/bg-login/BG7.jpg", "/img/bg-login/BG8.jpg", "/img/bg-login/BG9.jpg"];


  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false,
  });

  const [errors, setErrors] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const handleTogglePassword = (field: keyof typeof showPassword) => {
    console.log(`Toggling ${field} from ${showPassword[field]} to ${!showPassword[field]}`);
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // const validatePassword = (password: string): string => {
  //   if (password.length < 8) return "Minimal 8 karakter";
  //   if (!/[a-z]/.test(password)) return "Harus mengandung huruf kecil";
  //   if (!/[A-Z]/.test(password)) return "Harus mengandung huruf besar";
  //   if (!/\d/.test(password)) return "Harus mengandung angka";
  //   if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return "Harus mengandung karakter khusus";

  //   return ""; // Valid
  // };

  const validatePassword = (password: string) => {
    const requirements = [
      { regex: /.{8,}/, message: "Minimum 8 characters" },
      { regex: /[A-Z]/, message: "At least one uppercase letter" },
      { regex: /[a-z]/, message: "At least one lowercase letter" },
      { regex: /[0-9]/, message: "At least one number" },
      { regex: /[^A-Za-z0-9]/, message: "At least one special character" },
    ];

    const errors = requirements
      .filter((req) => !req.regex.test(password))
      .map((req) => req.message);

    return {
      isValid: errors.length === 0,
      messages: errors,
    };
  };

  // Update the validateForm function
  const validateForm = () => {
    let valid = true;
    const newErrors = {
      newPassword: "",
      confirmPassword: "",
    };

    // New password validation
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      newErrors.newPassword = passwordValidation.messages.join(", ");
      valid = false;
    }

    // Confirm password validation
    if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSignUp = async () => {
    // Simulasi daftar email yang sudah terdaftar
    // const registeredEmails = ["user@example.com", "admin@example.com"];

    // if (registeredEmails.includes(email)) {
    //   Swal.fire({
    //     icon: "warning",
    //     title: "Email Sudah Terdaftar",
    //     text: "Anda akan diarahkan ke halaman login.",
    //     confirmButtonColor: "#0F62FE",
    //   }).then(() => {
    //     navigate("/login"); // Redirect ke halaman login jika email sudah ada
    //   });
    // } 
    if (!validateForm()) return;

    if (password !== confirmPassword) {
      await Swal.fire({
        icon: "error",
        title: "Password Tidak Sama",
        text: "Pastikan password dan konfirmasi password sesuai.",
        confirmButtonColor: "#0F62FE",
      });
      return;
    }
    if (!agree) {
      await Swal.fire({
        icon: "error",
        title: "Persetujuan Diperlukan",
        text: "Anda harus menyetujui syarat dan ketentuan sebelum melanjutkan.",
        confirmButtonColor: "#0F62FE",
      });
      return;
    }

    try {
      const result = await registerUser({
        email: email,
        username: name,
        password: password,
      });

      Swal.fire({
        icon: "success",
        title: "Pendaftaran Berhasil!",
        text: "Silakan login menggunakan akun Anda.",
        confirmButtonColor: "#0F62FE",
      }).then(() => {
        navigate("/login");
      });
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: error.response?.data?.error || "Pendaftaran Gagal",
        text: error.response?.data?.details || "Terjadi kesalahan saat mendaftar.",
        confirmButtonColor: "#0F62FE",
      }).then(()=>{
        if (error.response?.data?.details == "Akun sudah terdaftar, silakan kembali ke menu login") navigate("/login");
      });
    }
  };

  const handleCaptchaVerify = (value) => {
    setIsCaptchaVerified(!!value);
  };

  const PasswordStrengthMeter = ({ password }: { password: string }) => {
    const requirements = [
      { regex: /.{8,}/, label: "8+ characters" },
      { regex: /[A-Z]/, label: "Uppercase letter" },
      { regex: /[a-z]/, label: "Lowercase letter" },
      { regex: /[0-9]/, label: "Number" },
      { regex: /[^A-Za-z0-9]/, label: "Special character" },
    ];

    const passedRequirements = requirements.filter((r) =>
      r.regex.test(password)
    );
    const strength = passedRequirements.length;

    return (
      <Box sx={{ mt: 1, mb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={(strength / requirements.length) * 100}
          color={
            strength <= 2 ? "error" : strength <= 3 ? "warning" : "success"
          }
          sx={{ height: 6, mb: 1 }}
        />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {requirements.map((req, i) => (
            <Chip
              key={i}
              label={req.label}
              size="small"
              color={req.regex.test(password) ? "success" : "default"}
              variant="outlined"
            />
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box className="login-container" sx={{ width: "100vw", height: "100vh", position: "relative", overflowX: "hidden", overflowY: "scroll" }}>
      {/* Background Carousel */}
      <Box
        className="login-background"
        sx={{
          width: "100%",
          height: "100%",
          position: "sticky",
          top: 0,
          left: 0,
          zIndex: 1,
          overflow: "hidden",
        }}
      >
        <AnimatePresence mode="sync">
          <motion.img
            key={images[currentImageIndex]}
            src={images[currentImageIndex]}
            alt="Background"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </AnimatePresence>
      </Box>

      {/* Login Form */}
      <Box className="login-form" sx={{
        width: "100%",
        maxWidth: "600px",
        padding: "40px",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        borderRadius: "12px",
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        zIndex: 10,
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h4" fontWeight={700} color="#21272A">
            Sign Up
          </Typography>
          <img
            src="/img/LOGO_SINERGISPP.png"
            alt="Company Logo"
            style={{ width: "70px" }}
          />
        </Box>
        {/* <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap:1 }}> */}
          <Box className="input-group" sx={{ width: "100%", maxWidth: "600px", textAlign: "left" }}>
            <Typography variant="body2" fontWeight={400} color="#21272A" textAlign='left'>
              Full Name
            </Typography>
            <TextField fullWidth variant="outlined" placeholder="Enter your full name" value={name} onChange={(e) => setName(e.target.value)} />
          </Box>
          <Box className="input-group" sx={{ width: "100%", maxWidth: "600px", textAlign: "left" }}>
            <Typography variant="body2" fontWeight={400} color="#21272A" textAlign='left'>
              Email Address
            </Typography>
            <TextField fullWidth variant="outlined" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Box>
        {/* </Box> */}
        <Box className="input-group" sx={{ width: "100%", maxWidth: "600px", textAlign: "left" }}>
          <Typography variant="body2" fontWeight={400} color="#21272A" textAlign='left'>
            Password
          </Typography>
          <TextField
            fullWidth
            type={showPassword.new ? "text" : "password"}
            variant="outlined"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => {
              const newPassword = e.target.value;
              setPassword(newPassword);
              const validation = validatePassword(e.target.value);
              setErrors((prev) => ({
                ...prev,
                newPassword: validation.messages.join(", "),
              }));
            }}
            error={!!errors.newPassword}
            helperText={errors.newPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleTogglePassword("new")}
                    edge="end"
                  >
                    {showPassword.new ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* Password Strength Meter - Only shows when typing */}
          {password && (
            <PasswordStrengthMeter password={password} />
          )}
        </Box>
        <Box className="input-group" sx={{ width: "100%", maxWidth: "600px", textAlign: "left" }}>
          <Typography variant="body2" fontWeight={400} color="#21272A" textAlign='left'>
            Confirm Password
          </Typography>
          <TextField
            fullWidth
            type={showPassword.confirm ? "text" : "password"}
            variant="outlined"
            placeholder="Confirm your password"
            value={confirmPassword}
            onChange={(e) => {
              const confirmPassword = e.target.value;
              setConfirmPassword(confirmPassword);
              // const validationMessage = validatePassword(confirmPassword);
              // setErrors(prev => ({ ...prev, confirmPassword: validationMessage }));
              // Real-time confirmation check
              if (
                confirmPassword &&
                confirmPassword !== password
              ) {
                setErrors((prev) => ({
                  ...prev,
                  confirmPassword: "Passwords do not match",
                }));
              } else {
                setErrors((prev) => ({ ...prev, confirmPassword: "" }));
              }
            }}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleTogglePassword("confirm")}
                    edge="end"
                  >
                    {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box className="signup-options" sx={{ width: "100%", maxWidth: "600px", display: "flex", alignItems: "center" }}>
          <FormControlLabel
            control={<Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)} />}
            label="I agree to the Terms & Conditions"
          />
        </Box>
        <ReCAPTCHA
          sitekey="6LeAcB8rAAAAAFX-8JOn82SIrzK7T5qmaN9XUQT_"  // Replace with your actual site key
          onChange={handleCaptchaVerify}
          onExpired={() => setIsCaptchaVerified(false)}
        />
        <Button fullWidth variant="contained" disabled={!isCaptchaVerified} sx={{ background: "#0F62FE", padding: "12px", fontSize: "16px", width: "100%", maxWidth: "600px" }} onClick={handleSignUp}>
          Sign Up
        </Button>
        <Divider sx={{ my: 2, width: "100%", maxWidth: "600px" }} />
        <Typography variant="body2" textAlign="center" color="#001D6C">
          Already have an account? <a href="/login">Log In</a>
        </Typography>
      </Box>


    </Box >
  );
};

export default SignUp;
